// src/pages/CrewCallPage.tsx — InfoHub Crew Call (BUILD_PLAN PR I-3). The page
// the Home crew-call banner deep-links to: event summary + roles (times, duties)
// + pick whole event or specific days/roles + confirm you've read the duties →
// submit availability to the Events Manager. Ported from events-live/hub-crewcall.jsx.
//
// Notes vs prototype (data we don't have in production, degraded gracefully):
//  - "You've done this" per-role stamp needs a per-user capability history we
//    don't store — omitted.
//  - "What to bring" has no column on Roles yet — the section hides when empty.
//  - Hero "Expected visitors" stat isn't on the event payload — omitted.

import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { C, F, EL } from "../theme";
import { ElIcon } from "../eventslive/ElIcon";
import { ensureElKeyframes } from "../eventslive/elKeyframes";
import { fmtDateRange, wdShort, dayNum, moShort } from "../eventslive/elFmt";
import { fetchOpenCall, submitOffer, withdrawOffer, type OpenCallResponse, type ELRole, type ELEventEntry } from "../lib/events";

export function CrewCallPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [data, setData] = useState<OpenCallResponse | null>(null);
  const [selId, setSelId] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);

  const [mode, setMode] = useState<"whole" | "days">("whole");
  const [days, setDays] = useState<Set<string>>(new Set());
  const [roles, setRoles] = useState<Set<number>>(new Set());
  const [anyRole, setAnyRole] = useState(false);
  const [read, setRead] = useState(false);
  const [note, setNote] = useState("");
  const [expanded, setExpanded] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [seededFor, setSeededFor] = useState<number | null>(null);
  const [confirmingWithdraw, setConfirmingWithdraw] = useState(false);

  useEffect(() => {
    ensureElKeyframes();
    let alive = true;
    fetchOpenCall()
      .then((d) => { if (alive) { setData(d); setLoading(false); } })
      .catch(() => { if (alive) { setData({ call: null }); setLoading(false); } });
    return () => { alive = false; };
  }, []);

  // All relevant events for the caller (multi-event). The active one is chosen by
  // the ?event= param, an explicit switch, or the primary (events[0]). Falls back
  // to a single synthesised entry if an older API is serving (deploy-gap safety).
  const entries = useMemo<ELEventEntry[]>(() => data?.events ?? (data?.event
    ? [{ event: data.event, call: data.call, roles: data.roles ?? [], myOffer: data.myOffer ?? null, myAssignments: data.myAssignments ?? [], myConfirmed: data.myConfirmed ?? [], signedOn: !!data.signedOn, signedOnAt: data.signedOnAt ?? null, state: null }]
    : []), [data]);
  const paramId = Number(params.get("event")) || null;
  const activeId = selId ?? paramId ?? entries[0]?.event.id ?? null;
  const entry = useMemo(() => entries.find((e) => e.event.id === activeId) ?? entries[0] ?? null, [entries, activeId]);

  // Seed the form from the active event + any existing offer; re-seeds on switch.
  useEffect(() => {
    if (!entry || seededFor === entry.event.id) return;
    const o = entry.myOffer;
    setMode(o?.mode ?? "whole");
    setDays(new Set(o && o.mode === "days" ? o.days : entry.event.days));
    setRoles(new Set(o?.anyRole ? [] : (o?.roleIds ?? [])));
    setAnyRole(o?.anyRole ?? false);
    setRead(o?.readConfirmed ?? false);
    setNote(o?.note ?? "");
    setConfirmingWithdraw(false);
    setSeededFor(entry.event.id);
  }, [entry, seededFor]);

  if (loading) {
    return <FullMessage title="Loading…" />;
  }
  if (!entry) {
    return <FullMessage title="No open call right now" sub="There's no event calling for volunteers at the moment. Check back soon." onBack={() => navigate("/")} />;
  }

  const event = entry.event;
  const eventRoles = entry.roles;
  const myOffer = entry.myOffer;
  const chosenDays = mode === "whole" ? event.days : event.days.filter((d) => days.has(d));
  // The caller already has an offer and has now unchecked every role → this is a
  // withdrawal, not an update: it removes them from the event (offer + any duties)
  // and reverts the Home banner to the original call invitation.
  const isWithdraw = !!myOffer && roles.size === 0 && !anyRole;
  const canSubmit = isWithdraw
    ? !saving
    : (anyRole || roles.size > 0) && read && (mode === "whole" || chosenDays.length > 0) && !saving;

  const toggleRole = (id: number) => setRoles((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleDay = (d: string) => setDays((p) => { const n = new Set(p); n.has(d) ? n.delete(d) : n.add(d); return n; });

  const submit = () => {
    if (isWithdraw) { setConfirmingWithdraw(true); return; }
    if (!canSubmit) return;
    setSaving(true);
    submitOffer({ eventId: event.id, mode, dayDates: mode === "whole" ? event.days : chosenDays, roleIds: anyRole ? [] : [...roles], anyRole, note: note.trim() || null, readConfirmed: read })
      .then(() => navigate("/"))
      .catch(() => setSaving(false));
  };

  const doWithdraw = () => {
    setSaving(true);
    withdrawOffer(event.id)
      .then(() => navigate("/"))
      .catch(() => { setSaving(false); setConfirmingWithdraw(false); });
  };

  return (
    <div style={{ flex: 1, display: "flex", flexDirection: "column", minHeight: "100vh", background: C.paper }}>
      {/* top bar */}
      <div style={{ flexShrink: 0, display: "flex", alignItems: "center", gap: 10, padding: "8px 14px 12px", background: C.paperSoft, borderBottom: `1px solid ${C.hairline}` }}>
        <button onClick={() => navigate("/")} style={{ width: 38, height: 38, borderRadius: 11, border: `1px solid ${C.hairline}`, background: C.card, display: "flex", alignItems: "center", justifyContent: "center", color: C.ink, cursor: "pointer" }}>
          <ElIcon name="chevron" size={18} style={{ transform: "scaleX(-1)" }} />
        </button>
        <div style={{ flex: 1 }}>
          <div style={{ fontFamily: F.body, fontSize: 10.5, fontWeight: 800, letterSpacing: 1.4, textTransform: "uppercase", color: EL.base }}>Events · Call for volunteers</div>
          <div style={{ fontFamily: F.serif, fontSize: 17, fontWeight: 700, color: C.ink, lineHeight: 1 }}>Offer your time</div>
        </div>
      </div>

      <div style={{ flex: 1, overflowY: "auto", padding: "14px 15px 24px", display: "flex", flexDirection: "column", gap: 14 }}>
        {/* event switcher — only when the caller is involved in more than one */}
        {entries.length > 1 && (
          <div>
            <div style={{ fontFamily: F.body, fontSize: 10.5, fontWeight: 800, letterSpacing: 1.4, textTransform: "uppercase", color: C.inkMute, marginBottom: 7 }}>You're involved in {entries.length} events — pick one</div>
            <div style={{ display: "flex", gap: 8, overflowX: "auto", paddingBottom: 2 }}>
              {entries.map((en) => {
                const on = en.event.id === event.id;
                const st = en.state ?? (en.myConfirmed?.length ? "live" : en.myOffer ? "pending" : "call");
                const dot = st === "live" ? EL.base : st === "pending" ? C.sage : EL.gold;
                return (
                  <button key={en.event.id} onClick={() => setSelId(en.event.id)} style={{ flexShrink: 0, display: "inline-flex", alignItems: "center", gap: 7, padding: "8px 13px", borderRadius: 999, border: `1.5px solid ${on ? EL.base : C.hairline}`, background: on ? EL.bg : C.card, cursor: "pointer" }}>
                    <span style={{ width: 8, height: 8, borderRadius: "50%", background: dot, flexShrink: 0 }} />
                    <span style={{ fontFamily: F.body, fontSize: 12.5, fontWeight: 700, color: on ? EL.base : C.ink, whiteSpace: "nowrap" }}>{en.event.name}</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}
        {/* event hero */}
        <div style={{ position: "relative", overflow: "hidden", borderRadius: 16, padding: "16px 16px 15px", background: `linear-gradient(150deg, ${EL.base}, ${EL.deep})`, color: "#fff", boxShadow: "0 8px 22px rgba(10,74,58,0.26)" }}>
          <div aria-hidden="true" style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: 6, background: `repeating-linear-gradient(135deg, ${EL.goldGlow} 0 6px, transparent 6px 12px)` }} />
          <div style={{ paddingLeft: 6 }}>
            <span style={{ display: "inline-block", fontFamily: F.mono, fontSize: 10, fontWeight: 700, letterSpacing: 0.5, color: EL.deep, background: EL.goldGlow, borderRadius: 6, padding: "2px 8px", textTransform: "uppercase" }}>{event.typeName}</span>
            <h2 style={{ fontFamily: F.serif, fontSize: 24, fontWeight: 800, margin: "8px 0 2px", letterSpacing: -0.5, lineHeight: 1.05 }}>{event.name}</h2>
            <div style={{ fontFamily: F.body, fontSize: 12.5, fontWeight: 700, color: EL.goldGlow }}>{fmtDateRange(event.days)}{event.location ? ` · ${event.location}` : ""}</div>
            {event.summary && <p style={{ fontFamily: F.body, fontSize: 13, lineHeight: 1.5, color: "rgba(255,255,255,0.9)", margin: "10px 0 0" }}>{event.summary}</p>}
            <div style={{ display: "flex", gap: 18, marginTop: 12, paddingTop: 11, borderTop: "1px dashed rgba(240,217,138,0.4)" }}>
              <HeroStat label="Days" value={String(event.days.length)} />
              <HeroStat label="Roles" value={String(eventRoles.length)} />
            </div>
          </div>
        </div>

        {/* availability mode */}
        <div>
          <Eyebrow>When can you help?</Eyebrow>
          <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
            <ModePill active={mode === "whole"} onClick={() => setMode("whole")} label="The whole event" sub={`All ${event.days.length} days`} />
            <ModePill active={mode === "days"} onClick={() => setMode("days")} label="Pick days" sub="Choose dates" />
          </div>
          {mode === "days" && (
            <div style={{ display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
              {event.days.map((d) => {
                const on = days.has(d);
                return (
                  <button key={d} onClick={() => toggleDay(d)} style={{ flex: "1 1 70px", borderRadius: 12, padding: "10px 4px", border: `1.5px solid ${on ? EL.base : C.hairline}`, background: on ? EL.bg : C.card, cursor: "pointer", textAlign: "center" }}>
                    <div style={{ fontFamily: F.body, fontSize: 10, fontWeight: 800, letterSpacing: 0.6, textTransform: "uppercase", color: on ? EL.base : C.inkMute }}>{wdShort(d)}</div>
                    <div style={{ fontFamily: F.serif, fontSize: 20, fontWeight: 700, color: on ? EL.base : C.ink, lineHeight: 1.1 }}>{dayNum(d)}</div>
                    <div style={{ fontFamily: F.body, fontSize: 10, fontWeight: 700, color: on ? EL.base : C.inkMute }}>{moShort(d)}</div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* roles */}
        <div>
          <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between" }}>
            <Eyebrow>Roles needed — pick what you can do</Eyebrow>
            <span style={{ fontFamily: F.mono, fontSize: 11, fontWeight: 700, color: anyRole || roles.size ? EL.base : C.inkMute }}>{anyRole ? "Any role" : `${roles.size} chosen`}</span>
          </div>

          {/* "Any role" — offer for whatever's needed rather than picking roles (item 3). */}
          <button
            type="button"
            onClick={() => setAnyRole((v) => !v)}
            style={{ marginTop: 9, width: "100%", display: "flex", alignItems: "center", gap: 11, textAlign: "left", border: `1.5px solid ${anyRole ? EL.base : C.hairline}`, background: anyRole ? C.paperSoft : C.card, borderRadius: 12, padding: "11px 13px", cursor: "pointer" }}
          >
            <span style={{ width: 22, height: 22, flexShrink: 0, borderRadius: 6, border: `1.5px solid ${anyRole ? EL.base : C.hairline}`, background: anyRole ? EL.base : "transparent", display: "flex", alignItems: "center", justifyContent: "center" }}>
              {anyRole && <ElIcon name="check" size={14} color="#fff" strokeWidth={2.6} />}
            </span>
            <span style={{ flex: 1 }}>
              <div style={{ fontFamily: F.body, fontSize: 13.5, fontWeight: 800, color: C.ink }}>Any role — put me where I'm needed</div>
              <div style={{ fontFamily: F.body, fontSize: 11.5, color: C.inkMute, marginTop: 1 }}>We'll place you in whatever role needs covering on the day.</div>
            </span>
          </button>

          {!anyRole && (
            <div style={{ display: "flex", flexDirection: "column", gap: 9, marginTop: 9 }}>
              {eventRoles.length === 0 && <div style={{ fontFamily: F.body, fontSize: 13, fontStyle: "italic", color: C.inkMute }}>No roles listed for this call yet.</div>}
              {eventRoles.map((r) => (
                <RoleCard key={r.id} role={r} chosen={roles.has(r.id)} expanded={expanded === r.id}
                  onExpand={() => setExpanded((e) => (e === r.id ? null : r.id))} onToggle={() => toggleRole(r.id)} />
              ))}
            </div>
          )}
        </div>

        {/* note */}
        <div>
          <Eyebrow>Anything we should know? (optional)</Eyebrow>
          <textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. I can only do mornings on Sunday, happy to cover gaps…"
            style={{ width: "100%", marginTop: 8, minHeight: 64, resize: "none", borderRadius: 12, border: `1px solid ${C.hairline}`, background: C.card, padding: "10px 12px", fontFamily: F.body, fontSize: 13, color: C.ink, outline: "none", boxSizing: "border-box" }} />
        </div>

        {/* confirm read */}
        <button onClick={() => setRead((r) => !r)} style={{ display: "flex", gap: 11, alignItems: "flex-start", textAlign: "left", border: `1.5px solid ${read ? EL.base : C.hairline}`, background: read ? EL.bg : C.card, borderRadius: 14, padding: "13px 14px", cursor: "pointer" }}>
          <span style={{ width: 24, height: 24, borderRadius: 7, flexShrink: 0, border: `1.5px solid ${read ? EL.base : C.hairline}`, background: read ? EL.base : C.card, display: "flex", alignItems: "center", justifyContent: "center", marginTop: 1 }}>{read && <ElIcon name="check" size={15} color="#fff" strokeWidth={2.6} />}</span>
          <span><span style={{ display: "block", fontFamily: F.body, fontSize: 13.5, fontWeight: 800, color: C.ink }}>I've read the role duties above</span><span style={{ display: "block", fontFamily: F.body, fontSize: 12, color: C.inkSoft, marginTop: 1 }}>You'll get a full safety briefing to acknowledge once a role is confirmed.</span></span>
        </button>

        {confirmingWithdraw ? (
          <div style={{ border: `1.5px solid ${C.signal}`, background: C.signalBg, borderRadius: 14, padding: "14px 15px" }}>
            <div style={{ fontFamily: F.body, fontSize: 14, fontWeight: 800, color: C.signalDk }}>Withdraw from {event.name}?</div>
            <div style={{ fontFamily: F.body, fontSize: 12.5, color: C.ink, marginTop: 4, lineHeight: 1.45 }}>This removes you from the event — including any duties you've already been given — and puts your roles back out to other volunteers. You can offer again any time while the call is open.</div>
            <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
              <button onClick={doWithdraw} disabled={saving} style={{ flex: 1, minHeight: 46, borderRadius: 12, border: "none", background: C.signal, color: "#fff", fontFamily: F.body, fontSize: 14, fontWeight: 800, cursor: saving ? "not-allowed" : "pointer", opacity: saving ? 0.7 : 1 }}>{saving ? "Withdrawing…" : "Yes, withdraw me"}</button>
              <button onClick={() => setConfirmingWithdraw(false)} disabled={saving} style={{ minHeight: 46, padding: "0 16px", borderRadius: 12, border: `1px solid ${C.hairline}`, background: C.card, color: C.ink, fontFamily: F.body, fontSize: 14, fontWeight: 800, cursor: "pointer" }}>Keep it</button>
            </div>
          </div>
        ) : (
          <>
            <button onClick={submit} disabled={!canSubmit} style={{ width: "100%", minHeight: 52, borderRadius: 14, border: "none", background: !canSubmit ? C.hairline : isWithdraw ? C.signal : `linear-gradient(150deg, ${EL.base}, ${EL.deep})`, color: canSubmit ? "#fff" : C.inkMute, fontFamily: F.body, fontSize: 15.5, fontWeight: 800, cursor: canSubmit ? "pointer" : "not-allowed", boxShadow: canSubmit && !isWithdraw ? "0 8px 20px rgba(10,74,58,0.3)" : "none", display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
              {saving ? "Sending…" : isWithdraw ? "Withdraw my availability" : myOffer ? "Update my availability" : "Mark me available"} {!isWithdraw && <ElIcon name="arrowRight" size={18} color={canSubmit ? "#fff" : C.inkMute} />}
            </button>
            {isWithdraw
              ? <div style={{ textAlign: "center", fontFamily: F.body, fontSize: 11.5, color: C.inkMute, marginTop: -6 }}>You've unchecked every role — this removes you from {event.name}.</div>
              : !canSubmit && !saving && <div style={{ textAlign: "center", fontFamily: F.body, fontSize: 11.5, color: C.inkMute, marginTop: -6 }}>Pick at least one role{mode === "days" ? ", a day," : ""} and confirm you've read the duties.</div>}
          </>
        )}
      </div>
    </div>
  );
}

function Eyebrow({ children }: { children: React.ReactNode }) {
  return <div style={{ fontFamily: F.body, fontSize: 10.5, fontWeight: 800, letterSpacing: 1.4, textTransform: "uppercase", color: C.inkMute }}>{children}</div>;
}

function HeroStat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div style={{ fontFamily: F.serif, fontSize: 22, fontWeight: 800, color: "#fff", lineHeight: 1 }}>{value}</div>
      <div style={{ fontFamily: F.body, fontSize: 9.5, fontWeight: 800, letterSpacing: 0.8, textTransform: "uppercase", color: EL.goldGlow, marginTop: 3 }}>{label}</div>
    </div>
  );
}

function ModePill({ active, onClick, label, sub }: { active: boolean; onClick: () => void; label: string; sub: string }) {
  return (
    <button onClick={onClick} style={{ flex: 1, borderRadius: 13, padding: "11px 12px", border: `1.5px solid ${active ? EL.base : C.hairline}`, background: active ? EL.bg : C.card, cursor: "pointer", textAlign: "left" }}>
      <div style={{ fontFamily: F.body, fontSize: 13.5, fontWeight: 800, color: active ? EL.base : C.ink }}>{label}</div>
      <div style={{ fontFamily: F.body, fontSize: 11, fontWeight: 600, color: active ? EL.base : C.inkMute, marginTop: 1 }}>{sub}</div>
    </button>
  );
}

function RoleCard({ role, chosen, expanded, onExpand, onToggle }: { role: ELRole; chosen: boolean; expanded: boolean; onExpand: () => void; onToggle: () => void }) {
  const times = role.start && role.end ? `${role.start}–${role.end}` : role.start ?? "";
  return (
    <div style={{ borderRadius: 14, border: `1.5px solid ${chosen ? EL.base : C.hairline}`, background: chosen ? EL.surf : C.card, overflow: "hidden" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 11, padding: "12px 13px" }}>
        <button onClick={onToggle} aria-pressed={chosen} style={{ width: 26, height: 26, borderRadius: 8, flexShrink: 0, border: `1.5px solid ${chosen ? EL.base : C.hairline}`, background: chosen ? EL.base : C.card, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}>{chosen && <ElIcon name="check" size={16} color="#fff" strokeWidth={2.6} />}</button>
        <button onClick={onExpand} style={{ flex: 1, minWidth: 0, border: "none", background: "none", textAlign: "left", cursor: "pointer", padding: 0 }}>
          <div style={{ fontFamily: F.serif, fontSize: 15.5, fontWeight: 700, color: C.ink }}>{role.name}</div>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 3 }}>
            {times && <span style={{ fontFamily: F.mono, fontSize: 11.5, fontWeight: 700, color: C.brassDk }}>{times}</span>}
            {role.need != null && <span style={{ fontFamily: F.body, fontSize: 11.5, color: C.inkMute }}>· {role.need} needed / day</span>}
          </div>
        </button>
        <span onClick={onExpand} style={{ color: C.inkMute, cursor: "pointer", transform: expanded ? "rotate(180deg)" : "none", transition: "transform .2s" }}><ElIcon name="chevronDown" size={18} color={C.inkMute} /></span>
      </div>
      {expanded && (role.duties.length > 0 || role.bring.length > 0) && (
        <div style={{ padding: "0 13px 13px 50px", animation: "el-fade .2s ease both" }}>
          <div style={{ borderTop: `1px dashed ${C.hairline}`, paddingTop: 10 }}>
            {role.duties.length > 0 && <DutyList icon="clipboard" label="What you'll do" items={role.duties} />}
            {role.duties.length > 0 && role.bring.length > 0 && <div style={{ height: 10 }} />}
            {role.bring.length > 0 && <DutyList icon="shield" label="What to bring" items={role.bring} />}
          </div>
        </div>
      )}
    </div>
  );
}

function DutyList({ icon, label, items }: { icon: string; label: string; items: string[] }) {
  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
        <ElIcon name={icon} size={13} color={EL.base} />
        <span style={{ fontFamily: F.body, fontSize: 10.5, fontWeight: 800, letterSpacing: 0.8, textTransform: "uppercase", color: EL.base }}>{label}</span>
      </div>
      <ul style={{ margin: 0, padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: 5 }}>
        {items.map((it, i) => (
          <li key={i} style={{ display: "flex", gap: 8, fontFamily: F.body, fontSize: 12.5, color: C.inkSoft, lineHeight: 1.4 }}>
            <span style={{ width: 5, height: 5, borderRadius: "50%", background: C.brassLt, flexShrink: 0, marginTop: 6 }} />{it}
          </li>
        ))}
      </ul>
    </div>
  );
}

function FullMessage({ title, sub, onBack }: { title: string; sub?: string; onBack?: () => void }) {
  return (
    <div style={{ minHeight: "100vh", background: C.paper, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 10, padding: 24, textAlign: "center" }}>
      <div style={{ fontFamily: F.serif, fontSize: 20, fontWeight: 700, color: C.ink }}>{title}</div>
      {sub && <div style={{ fontFamily: F.body, fontSize: 13.5, color: C.inkSoft, maxWidth: 320 }}>{sub}</div>}
      {onBack && <button onClick={onBack} style={{ marginTop: 8, border: `1px solid ${C.hairline}`, background: C.card, color: C.ink, borderRadius: 11, padding: "9px 18px", fontFamily: F.body, fontWeight: 800, fontSize: 13, cursor: "pointer" }}>Back to Home</button>}
    </div>
  );
}
